import CheckIcon from "@/assets/svgs/CheckIcon";
import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { useI18nContext } from "@/hooks/useI18nContext";
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { Icon, Typography } from "@graphy/ui";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { OnboardingDestinationEnum } from "../../shared/types/common.types";

export enum GraphyPlanType {
  Launch = "Launch",
  Rise = "Rise",
  Scale = "Scale",
}
export type PlansType = {
  planName: GraphyPlanType;
  amount: { month: string; year: string; global?: string };
  bestFor: string;
  recommened: boolean;
  ctas: CtaType[];
  features: string[];
  billingCycle?: { value: "month" | "year", label: string }[] | "month" | "year",
  hideCTA?: boolean,
  showBilledAnnually?: boolean,
  isIndiaPricing?: boolean
};

const PricingCard = ({
  planName,
  amount,
  bestFor,
  recommened,
  ctas,
  features,
  billingCycle,
  hideCTA = false,
  showBilledAnnually = false,
  isIndiaPricing = false
}: PlansType) => {

  const isUsLocale = useI18nContext().locale === "us";
  const router = useRouter();

  let featuresNew = [...features];
  let updatedCtas = [...ctas];
  
  const isUSMembershipPage =  isUsLocale && router.asPath.includes('page/memberships/')
  
  if (planName === 'Launch') {
    featuresNew = featuresNew.filter(item => item !== "Android or iOS app");
  } else if (planName === 'Scale') {
    if (!isUSMembershipPage) {
      featuresNew = [...featuresNew];
    }
  }

  const getAmount = () => isIndiaPricing ? amount.year : (amount as any).global || (billingCycle === "month" ? (amount as any).month : amount.year);

  // Update CTA destinations based on billing cycle
  if (!hideCTA && billingCycle) {
    updatedCtas = ctas.map(cta => {
      const newCta = { ...cta };
      if (!newCta.queryParams) {
        newCta.queryParams = {};
      }
      
      const isYearly = billingCycle === "year";
      
      switch (planName) {
        case GraphyPlanType.Launch:
          newCta.queryParams.destination = isYearly ? OnboardingDestinationEnum.PricingLaunchYearly : OnboardingDestinationEnum.PricingLaunchMonthly;
          break;
        case GraphyPlanType.Rise:
          newCta.queryParams.destination = isYearly ? OnboardingDestinationEnum.PricingRiseYearly : OnboardingDestinationEnum.PricingRiseMonthly;
          break;
        case GraphyPlanType.Scale:
          newCta.queryParams.destination = isYearly ? OnboardingDestinationEnum.PricingScaleYearly : OnboardingDestinationEnum.PricingScaleMonthly;
          break;
      }
      return newCta;
    });
  }

  return (
    <div
      className={`flex w-full flex-col overflow-hidden flex-1`}
    >
      {recommened && (
        <div className="bg-purple-1 !py-1 text-center text-body font-semibold" style={{ color: "rgba(42, 40, 97, 1)" }}>
          Recommended
        </div>
      )}
      <div
        className={`w-full flex flex-col flex-1 ${recommened ? "bg-purple-0" : "bg-white"}`}
      >
        <div
          className={`border-b border-grey-2 ${
            isIndiaPricing
              ? recommened
                ? "px-6 pt-3 "
                : "px-6 pt-10 "
              : recommened
                ? "px-6 pb-6 pt-3"
                : "p-6 pt-10"
          }`}
        >
          <div className="mb-4 flex-col gap-1">
            <div
              className={` py-1 text-title5  font-semibold text-darkSlate01 `}
            >
              {planName}
            </div>

            <div
              className={`py-1 text-caption font-normal `}
              color="text-darkSlate04"
            >
              {bestFor}
            </div>
          </div>
          <div className="mb-4 flex items-end justify-between py-1">
            <div className="flex flex-row items-end ">
              <Typography
                size="TITLE_1"
                fontWeight="font-medium"
                colorClass="text-darkSlate01"
              >
                {getAmount()}
              </Typography>
              <span className=" text-title6 font-semibold text-darkSlate01">
                 per month
              </span>
            </div>
          </div>
          {showBilledAnnually && (
            <div className="text-[16px] leading-[24px]" style={{ color: "rgba(58, 61, 80, 1)" }}>
              *billed annually
            </div>
          )}
        </div>
        <div className="px-6 pb-4 flex flex-col flex-1">
          {/* Only show KEY FEATURES and check icons if not using India-style data structure */}
          {!('global' in amount) && (
            <>
              <div className="mb-4 mt-2 flex items-center font-inter font-normal text-[14px] leading-[20px] tracking-[0.005em] text-[#393F41] align-middle">
                KEY FEATURES
              </div>
              <div className="flex flex-col gap-4">
                {featuresNew.map((feature: string) => {
                  if (feature === "Basic integrations") {
                    return (
                      <Fragment key={feature}>
                        <div className="text-body font-medium text-darkSlate01 flex items-center gap-2">
                          <span><CheckIcon /></span>
                          <span>Basic integrations</span>
                          <Icon
                            icon={faCircleInfo}
                            data-tooltip-id="Basicintegrations"
                            className="cursor-pointer"
                          />
                        </div>
                        <ReactTooltip
                          id="Basicintegrations"
                          place="bottom"
                          content="Google Analytics, Facebook Pixel, Vimeo, Sprout video, Zapier, Pabbly, GTM, Zoom"
                          className="!w-[280px] !h-25"
                        />
                      </Fragment>
                    );
                  }
                  if (feature.startsWith("Advanced integrations")) {
                    return (
                      <Fragment key={feature}>
                        <div className="text-body font-medium text-darkSlate01 flex items-center gap-2">
                          <span><CheckIcon /></span>
                          <span>{feature}</span>
                          <Icon
                            icon={faCircleInfo}
                            data-tooltip-id="AdvancedIntegrations"
                            className="cursor-pointer"
                          />
                        </div>
                        <ReactTooltip
                          id="AdvancedIntegrations"
                          place="bottom"
                          content="Everything in Launch plus Email integration- Mailchimp, Whatsapp widget, Fomo, Zoho CRM"
                          className="!w-[280px] !h-25"
                        />
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={feature}>
                      <div className="text-body font-medium text-darkSlate01 flex items-center gap-2">
                        <span><CheckIcon /></span>
                        {feature}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </>
          )}
          {/* For India-style data structure, show features as plain text only, with specific styles */}
          {('global' in amount) && (
            <div className="flex flex-col gap-2 mt-2">
              {/* 10%/5% per sale */}
              <div className="flex items-end gap-1">
                <span className="font-semibold text-[24px] leading-[32px] tracking-[-0.5px] align-bottom text-[#393F41]">
                  {(featuresNew[0] as any)[isIndiaPricing ? 'india' : 'global'] || featuresNew[0]}
                </span>
                <span className="font-semibold text-[16px] leading-[24px] tracking-[-0.5px] align-bottom text-[#393F41]">
                  per sale
                </span>
              </div>
              {/* or ₹10 whichever is higher */}
              <div className="font-normal text-[14px] leading-[20px] tracking-[0px] text-[rgba(58,61,80,1)]">
                {(featuresNew[1] as any)[isIndiaPricing ? 'india' : 'global'] || featuresNew[1]}
              </div>
              {/* Best for... */}
              <div className="font-normal text-[16px] leading-[24px] tracking-[0.5px] text-[rgba(58,61,80,1)] font-inter">
                {featuresNew[2]}
              </div>
            </div>
          )}
        </div>
        {!hideCTA && <div className="px-6 pb-6"><CtaButtonGroup ctas={updatedCtas} align="stretch" fullWidth={true} /></div>}
      </div>
    </div>
  );
};

export default PricingCard;
